/**
 * /downhole/run-logs — every run in the company (fieldiq-api#882 `GET /runs`),
 * full width. A row opens the run on its own page, /downhole/run-logs/:runId
 * (RunLogDetailScreen), where Close Run, Edit and Delete live. No panel opens
 * beside the list: split panes are banned (CLAUDE.md). A legacy `?run=<id>`
 * link (emails, bookmarks) is forwarded to that page.
 *
 * A run is LOGGED from its assembly (the builder's Runs panel), because a run
 * belongs to one string and the API's route is /assemblies/:id/runs — this
 * list has no assembly to log against.
 *
 * The date filter is the RUN-IN day in the tenant's zone, which is what the
 * API filters on (`from` / `to`, YYYY-MM-DD). It lives in the URL (`?from=`,
 * `?to=`) so the back arrow on a run's page returns to the same range. Every
 * measured figure is imperial as stored and printed in the tenant's unit
 * system.
 */
import React, { useMemo } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, Loader2 } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useDownholeRuns } from '../../hooks/useDownhole';
import { formatInstantDay } from '../../lib/calendar-date';
import { fmtHours } from '../../lib/display-labels';
import { formatDh } from '../../lib/downhole-units';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { DH_DATE_INPUT, DH_SECONDARY, DhEmpty, DhField, DhPanel, RunStatePill } from './DownholeBits';
import { useOpenRecord } from './recordPage';

const DAY = /^\d{4}-\d{2}-\d{2}$/;

export function wellRig(well: string | null, rig: string | null): string {
  if (!well && !rig) return '—';
  return `${well || '—'} / ${rig || '—'}`;
}

export function LostInHole() {
  return (
    <span className="rounded-fi-pill border border-black/20 bg-fi-signal-red px-2 py-0.5 text-[0.62rem] font-black uppercase text-fi-signal-ink">
      Lost in hole
    </span>
  );
}

export default function RunLogsScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [searchParams, setSearchParams] = useSearchParams();
  const legacyRun = searchParams.get('run');
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';
  // replace, not push: typing a date is not a step the back button should replay.
  const setDates = (patch: { from?: string; to?: string }) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setSearchParams(next, { replace: true });
  };

  // Only a complete day reaches the API; a half-typed date is not a filter.
  const params = useMemo(
    () => ({ ...(DAY.test(from) && { from }), ...(DAY.test(to) && { to }) }),
    [from, to],
  );
  const query = useDownholeRuns(params);
  const failure = queryLoadFailure(query);
  const runs = query.data?.data ?? [];
  const meta = query.data?.meta;
  const units = meta?.unit_system ?? null;
  const tz = meta?.time_zone ?? null;
  const filtered = Object.keys(params).length > 0;

  const select = (id: string) => openRecord(`/downhole/run-logs/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      select(id);
    }
  };

  if (legacyRun) return <Navigate to={`/downhole/run-logs/${legacyRun}`} replace />;

  return (
    <div data-testid="downhole-run-logs-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      {/* No stat strip: the register below is the whole set, and a count of it
          is the footer's job. */}
      <PageHeaderBand eyebrow="Downhole tools" title="Run Logs" onBack={() => navigate(-1)} backLabel="Back" />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-44">
              <DhField label="Run in from">
                <input type="date" className={DH_DATE_INPUT} value={from} max={to || undefined} onChange={(e) => setDates({ from: e.target.value })} />
              </DhField>
            </div>
            <div className="w-44">
              <DhField label="Run in to">
                <input type="date" className={DH_DATE_INPUT} value={to} min={from || undefined} onChange={(e) => setDates({ to: e.target.value })} />
              </DhField>
            </div>
            {(from || to) && (
              <button
                type="button"
                className={DH_SECONDARY}
                onClick={() => setDates({ from: '', to: '' })}
              >
                Clear dates
              </button>
            )}
          </div>

          <DhPanel
            eyebrow="Downhole runs"
            title="Run log"
            testId="downhole-run-register"
            footer={
              !query.isLoading && !failure && runs.length > 0
                ? `${runs.length} ${runs.length === 1 ? 'run' : 'runs'}${filtered ? ` run in ${params.from ?? 'any day'} – ${params.to ?? 'today'} (${tz ?? 'tenant time'})` : ''}`
                : undefined
            }
          >
            {query.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading runs" />
              </div>
            ) : failure ? (
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="run logs" />
            ) : runs.length === 0 ? (
              <DhEmpty>{filtered ? 'No run went in between those days.' : 'No runs have been logged.'}</DhEmpty>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[880px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr>
                        <th scope="col" className="px-4 py-3">Run in</th>
                        <th scope="col" className="px-4 py-3">Assembly</th>
                        <th scope="col" className="px-4 py-3">Run</th>
                        <th scope="col" className="px-4 py-3">Well / Rig</th>
                        <th scope="col" className="px-4 py-3 text-right">Depth out</th>
                        <th scope="col" className="px-4 py-3 text-right">Footage</th>
                        <th scope="col" className="px-4 py-3 text-right">Circ. hrs</th>
                        <th scope="col" className="w-[44px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {runs.map((r) => (
                        <tr
                          key={r.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => select(r.id)}
                          onKeyDown={rowKeys(r.id)}
                          className="cursor-pointer border-t border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10"
                        >
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{formatInstantDay(r.run_in_at, tz) ?? '—'}</td>
                          <td className="px-4 py-3">
                            <p className="whitespace-nowrap font-mono text-sm font-extrabold text-fieldiq-accent-signal">{r.assembly_number || '—'}</p>
                            <p className="max-w-[16rem] truncate text-xs text-fieldiq-muted">{r.assembly_name || '—'}</p>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">
                            {displayValue(r.run_number)} <RunStatePill state={r.run_state} /> {r.lih_flag && <LostInHole />}
                          </td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{wellRig(r.well_name, r.rig_name)}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm text-fieldiq-text">{formatDh(r.depth_out_ft, 'length', units, { digits: 0 })}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm text-fieldiq-text">{formatDh(r.footage_drilled, 'length', units, { digits: 0 })}</td>
                          <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{fmtHours(r.circulating_hours, 1)}</td>
                          <td className="px-4 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 p-4 md:hidden">
                  {runs.map((r) => (
                    <div
                      key={r.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => select(r.id)}
                      onKeyDown={rowKeys(r.id)}
                      className="cursor-pointer rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <p className="font-mono text-sm font-extrabold text-fieldiq-accent-signal">
                          {r.assembly_number || '—'} · run {displayValue(r.run_number)}
                        </p>
                        <span className="flex gap-1">
                          <RunStatePill state={r.run_state} />
                          {r.lih_flag && <LostInHole />}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-fieldiq-text">{wellRig(r.well_name, r.rig_name)}</p>
                      <p className="mt-1 text-xs text-fieldiq-muted">
                        In {formatInstantDay(r.run_in_at, tz) ?? '—'} · footage {formatDh(r.footage_drilled, 'length', units, { digits: 0 })}
                      </p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </DhPanel>

        </div>
      </section>
    </div>
  );
}
